import * as React from "react";

import selectedActions from "@impero-com/ui/img/selected-actions-arrow.svg";

import exportIcon from "../../img/export.svg";
import * as DV from "../../utils/dataView";
import { ExportMetadata } from "../../utils/export";
import { assertNever } from "../../utils/obj";
import { Toolbar } from "../toolbar";
import type { ImageDividerSpec, ToolSpec, SpacerToolSpec } from "../toolbar";

import { FormattedView } from "./columns";
import * as theme from "./theme.css";
import { ViewAdjuster } from "./viewAdjuster";

class DataTableToolbar<ITEM> extends React.PureComponent<
    DataTableToolbarProps<ITEM>
> {
    render = () => {
        const selectionTools = this.buildSelectionTools();
        let tools: Array<ToolSpec> = selectionTools ?? [];
        if (this.props.tools) {
            tools = tools.concat(this.buildStandardTools());
        }
        return <Toolbar tools={tools} />;
    };

    private buildSelectionTools = () => {
        const {
            formattedView,
            hasExpansionColumn,
            selectionTools,
            visibleColumnIds,
        } = this.props;
        if (!selectionTools) {
            return [];
        }
        const { selectedRowIds, onSelectionActionDone } = selectionTools;
        const tools: Array<ToolSpec> = [];
        if (hasExpansionColumn) {
            tools.push({
                type: "Dynamic",
                getTool: () => (
                    <div className={theme.expansionColumnToolbarPadder}>
                        {"\xa0"}
                    </div>
                ),
            });
        }
        tools.push({
            type: "ImageDivider",
            image: selectedActions,
        });
        return tools.concat(
            selectionTools.tools.map((toolSpec) => {
                switch (toolSpec.type) {
                    case "Text":
                    case "Icon":
                    case "Image":
                        return {
                            ...toolSpec,
                            isEnabled: () => {
                                const enabled = selectedRowIds.length > 0;
                                if (!enabled) {
                                    return false;
                                } else if (toolSpec.isEnabled) {
                                    return toolSpec.isEnabled(
                                        formattedView,
                                        selectedRowIds,
                                        visibleColumnIds
                                    );
                                } else {
                                    return true;
                                }
                            },
                            onCommand: () =>
                                toolSpec.onCommand(
                                    onSelectionActionDone,
                                    formattedView,
                                    selectedRowIds,
                                    visibleColumnIds
                                ),
                        };
                    case "Dynamic":
                        return {
                            ...toolSpec,
                            getTool: () =>
                                toolSpec.getTool(
                                    onSelectionActionDone,
                                    formattedView,
                                    selectedRowIds,
                                    visibleColumnIds
                                ),
                        };
                    case "Toggle":
                        return {
                            ...toolSpec,
                            isEnabled: () => {
                                const enabled = selectedRowIds.length > 0;
                                if (!enabled) {
                                    return false;
                                } else if (toolSpec.isEnabled) {
                                    return toolSpec.isEnabled(
                                        formattedView,
                                        selectedRowIds,
                                        visibleColumnIds
                                    );
                                } else {
                                    return true;
                                }
                            },
                            onCommand: (toggleId: any) =>
                                toolSpec.onCommand(
                                    toggleId,
                                    onSelectionActionDone,
                                    formattedView,
                                    selectedRowIds,
                                    visibleColumnIds
                                ),
                        };
                    case "ImageDivider":
                    case "Spacer":
                        return toolSpec;
                    default:
                        return assertNever(toolSpec);
                }
            })
        );
    };

    private buildStandardTools = () => {
        const {
            tools,
            formattedView,
            visibleColumnIds,
            onVisibleColumnsChanged,
        } = this.props;
        return buildStandardTools(
            tools,
            formattedView,
            visibleColumnIds,
            onVisibleColumnsChanged
        );
    };
}

interface DataTableToolbarProps<ITEM> {
    formattedView: FormattedView<ITEM>;
    hasExpansionColumn: boolean;
    selectionTools: SelectionTools<ITEM> | undefined;
    visibleColumnIds: Set<string>;
    tools: Array<StandardToolSpec<ITEM>> | undefined;
    onVisibleColumnsChanged: (visibleColumnIds: Set<string>) => void;
}

interface SelectionTools<ITEM> {
    selectedRowIds: Array<string>;
    tools: Array<SelectionToolSpec<ITEM>>;
    onSelectionActionDone: () => void;
}

interface SelectionHandler<ITEM> {
    canSelect?: (item: ITEM) => boolean;
    onSelectionChange?: (selectedIds: Array<string>) => void;
    tools?: Array<SelectionToolSpec<ITEM>>;
}

interface BaseSelectionToolSpec<ITEM> {
    label: string;
    onCommand: (
        onSelectionActionDone: () => void,
        formattedView: FormattedView<ITEM>,
        selectedIds: Array<string>,
        visibleColumnIds: Set<string>
    ) => void;
    isEnabled?: (
        formattedView: FormattedView<ITEM>,
        selectedIds: Array<string>,
        visibleColumnIds: Set<string>
    ) => boolean;
}

interface TextSelectionToolSpec<ITEM> extends BaseSelectionToolSpec<ITEM> {
    type: "Text";
}

interface IconSelectionToolSpec<ITEM>
    extends BaseSelectionToolSpec<ITEM>, IconTool {
    type: "Icon";
}

interface ImageSelectionToolSpec<ITEM>
    extends BaseSelectionToolSpec<ITEM>, ImageTool {
    type: "Image";
}

interface DynamicSelectionToolSpec<ITEM> {
    type: "Dynamic";
    getTool: (
        onSelectionActionDone: () => void,
        formattedView: FormattedView<ITEM>,
        selectedIds: Array<string>,
        visibleColumnIds: Set<string>
    ) => React.ReactNode;
}

interface BaseToggleButton<ID> {
    id: ID;
}

interface IconToggleButton<ID> extends BaseToggleButton<ID>, IconTool {
    type: "IconToggle";
}

interface ImageToggleButton<ID> extends BaseToggleButton<ID>, ImageTool {
    type: "ImageToggle";
}

interface ToggleSelectionToolSpec<ID, ITEM> {
    type: "Toggle";
    toggleButtons: Array<ToggleButton<ID>>;
    activeButtonId: ID;
    onCommand: (
        toggleId: ID,
        onSelectionActionDone: () => void,
        formattedView: FormattedView<ITEM>,
        selectedRowIds: Array<string>,
        visibleColumnIds: Set<string>
    ) => void;
    isEnabled?: (
        formattedView: FormattedView<ITEM>,
        selectedRowIds: Array<string>,
        visibleColumnIds: Set<string>
    ) => boolean;
}

type SelectionToolSpec<ITEM> =
    | TextSelectionToolSpec<ITEM>
    | IconSelectionToolSpec<ITEM>
    | ImageSelectionToolSpec<ITEM>
    | DynamicSelectionToolSpec<ITEM>
    | ToggleSelectionToolSpec<any, ITEM>
    | ImageDividerSpec
    | SpacerToolSpec;

interface BaseStandardToolSpec<ITEM> {
    label: string;
    onCommand: (
        formattedView: FormattedView<ITEM>,
        visibleColumnIds: Set<string>
    ) => void;
    isEnabled?: (
        formattedView: FormattedView<ITEM>,
        visibleColumnIds: Set<string>
    ) => boolean;
}

interface TextStandardToolSpec<ITEM> extends BaseStandardToolSpec<ITEM> {
    type: "Text";
}

interface IconTool {
    icon: string;
}

interface ImageTool {
    image: string;
    imageClassName?: string;
}

interface IconStandardToolSpec<ITEM>
    extends BaseStandardToolSpec<ITEM>, IconTool {
    type: "Icon";
}

interface ImageStandardToolSpec<ITEM>
    extends BaseStandardToolSpec<ITEM>, ImageTool {
    type: "Image";
}

interface DynamicStandardToolSpec<ITEM> {
    type: "Dynamic";
    getTool: (
        formattedView: FormattedView<ITEM>,
        visibleColumnIds: Set<string>
    ) => React.ReactNode;
}

interface BaseToggleButton<ID> {
    id: ID;
}

interface IconToggleButton<ID> extends BaseToggleButton<ID>, IconTool {
    type: "IconToggle";
}

interface ImageToggleButton<ID> extends BaseToggleButton<ID>, ImageTool {
    type: "ImageToggle";
}

type ToggleButton<ID> = IconToggleButton<ID> | ImageToggleButton<ID>;

interface ToggleStandardToolSpec<ID, ITEM> {
    type: "Toggle";
    toggleButtons: Array<ToggleButton<ID>>;
    activeButtonId: ID;
    onCommand: (
        toggleId: ID,
        formattedView: FormattedView<ITEM>,
        visibleColumnIds: Set<string>
    ) => void;
    isEnabled?: (
        formattedView: FormattedView<ITEM>,
        visibleColumnIds: Set<string>
    ) => boolean;
}

interface ViewAdjusterToolSpec {
    type: "ViewAdjuster";
    buildChildren?: () => React.ReactNode;
}

type StandardToolSpec<ITEM> =
    | TextStandardToolSpec<ITEM>
    | IconStandardToolSpec<ITEM>
    | ImageStandardToolSpec<ITEM>
    | DynamicStandardToolSpec<ITEM>
    | ToggleStandardToolSpec<any, ITEM>
    | ImageDividerSpec
    | SpacerToolSpec
    | ViewAdjusterToolSpec;

function buildStandardTools<ITEM>(
    tools: Array<StandardToolSpec<ITEM>> | undefined,
    formattedView: FormattedView<ITEM>,
    visibleColumnIds: Set<string>,
    onVisibleColumnsChanged: (visibleColumnIds: Set<string>) => void
) {
    if (!tools) {
        return [];
    }
    const result: Array<ToolSpec> = tools.map((toolSpec) => {
        switch (toolSpec.type) {
            case "Text":
            case "Icon":
            case "Image":
                return {
                    ...toolSpec,
                    isEnabled: () => {
                        if (toolSpec.isEnabled) {
                            return toolSpec.isEnabled(
                                formattedView,
                                visibleColumnIds
                            );
                        } else {
                            return true;
                        }
                    },
                    onCommand: () => {
                        toolSpec.onCommand(formattedView, visibleColumnIds);
                    },
                };
            case "Dynamic":
                return {
                    ...toolSpec,
                    getTool: () =>
                        toolSpec.getTool(formattedView, visibleColumnIds),
                };
            case "Toggle":
                return {
                    ...toolSpec,
                    isEnabled: () => {
                        if (toolSpec.isEnabled) {
                            return toolSpec.isEnabled(
                                formattedView,
                                visibleColumnIds
                            );
                        } else {
                            return true;
                        }
                    },
                    onCommand: (toggleId: any) => {
                        toolSpec.onCommand(
                            toggleId,
                            formattedView,
                            visibleColumnIds
                        );
                    },
                };
            case "ViewAdjuster":
                return {
                    type: "Dynamic",
                    getTool: () => (
                        <ViewAdjuster<ITEM>
                            columns={formattedView.columns}
                            visibleColumnIds={visibleColumnIds}
                            onChange={onVisibleColumnsChanged}
                        >
                            {toolSpec.buildChildren
                                ? toolSpec.buildChildren()
                                : undefined}
                        </ViewAdjuster>
                    ),
                };
            case "ImageDivider":
            case "Spacer":
                return toolSpec;
            default:
                return assertNever(toolSpec);
        }
    });
    return result;
}

function buildStandardExportTool<ITEM>(
    fileName: string,
    ext: string,
    fileTitle: string,
    newExportMetadata: () => ExportMetadata
): StandardToolSpec<ITEM> {
    return {
        type: "Image",
        label: "",
        image: exportIcon,
        onCommand: (
            formattedView: FormattedView<ITEM>,
            visibleColumnIds: Set<string>
        ) => {
            const displayedColumns = formattedView.columns.filter((col) =>
                visibleColumnIds.has(col.extractor.id)
            );
            DV.exportToExcel(
                formattedView.dataView,
                () => true,
                displayedColumns.map((column) => {
                    return {
                        label: column.extractor.label,
                        getValue: (item: ITEM) => column.getCopyableValue(item),
                    };
                }),
                fileName,
                ext,
                fileTitle,
                newExportMetadata()
            );
        },
    };
}

function buildSelectionExportTool<ITEM>(
    fileName: string,
    ext: string,
    fileTitle: string,
    newExportMetadata: () => ExportMetadata
): SelectionToolSpec<ITEM> {
    return {
        type: "Image",
        label: "",
        image: exportIcon,
        onCommand: (
            onSelectionActionDone,
            formattedView: FormattedView<ITEM>,
            selectedRowIds: Array<string>,
            visibleColumnIds: Set<string>
        ) => {
            if (selectedRowIds.length === 0) {
                return;
            }
            const displayedColumns = formattedView.columns.filter((col) =>
                visibleColumnIds.has(col.extractor.id)
            );
            const { dataView } = formattedView;
            const { dataSource } = dataView;
            const selectedRowIdsSet = new Set(selectedRowIds);
            const filteredDataSource = dataSource.filtered((item) =>
                selectedRowIdsSet.has(dataSource.getId(item))
            );
            const filteredDataView: DV.DataView<ITEM> = {
                ...dataView,
                dataSource: filteredDataSource,
            };
            DV.exportToExcel(
                filteredDataView,
                () => true,
                displayedColumns.map((column) => {
                    return {
                        label: column.extractor.label,
                        getValue: (item: ITEM) => column.getCopyableValue(item),
                    };
                }),
                fileName,
                ext,
                fileTitle,
                newExportMetadata()
            );
            onSelectionActionDone();
        },
        isEnabled: (
            _formattedView: FormattedView<ITEM>,
            selectedRowIds: Array<string>,
            _visibleColumnIds: Set<string>
        ) => {
            return selectedRowIds.length > 0;
        },
    };
}

function buildToolbarWithViewAdjuster<ITEM>(
    buildAdjusterChildren?: () => React.ReactNode
): Array<StandardToolSpec<ITEM>> {
    return [
        {
            type: "Spacer",
            flex: "auto",
        },
        {
            type: "ViewAdjuster",
            buildChildren: buildAdjusterChildren,
        },
    ];
}
export type {
    SelectionHandler,
    SelectionToolSpec,
    TextSelectionToolSpec,
    IconSelectionToolSpec,
    ImageSelectionToolSpec,
    DynamicSelectionToolSpec,
    ToggleSelectionToolSpec,
    StandardToolSpec,
    TextStandardToolSpec,
    IconStandardToolSpec,
    ImageStandardToolSpec,
    DynamicStandardToolSpec,
    ToggleStandardToolSpec,
    ViewAdjusterToolSpec,
    DataTableToolbarProps,
    SelectionTools,
};
export {
    ImageDividerSpec,
    SpacerToolSpec,
    DataTableToolbar,
    buildStandardTools,
    buildSelectionExportTool,
    buildStandardExportTool,
    buildToolbarWithViewAdjuster,
};
